import { useState } from 'react';
import { Link } from 'react-router-dom';
import { createPageUrl } from './utils';
import { LayoutDashboard, Upload, Target, TrendingUp, Menu, X, Wallet, LogOut } from 'lucide-react';
import { useAuth } from '@/lib/AuthContext';

export default function Layout({ children, currentPageName }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout } = useAuth();

  const navItems = [
    { name: 'Dashboard', icon: LayoutDashboard, page: 'Dashboard' },
    { name: 'Upload', icon: Upload, page: 'Upload' },
    { name: 'Budget', icon: Wallet, page: 'Budget' },
    { name: 'Goals', icon: Target, page: 'Goals' },
  ];

  const NavLinks = () => (
    <nav className="flex-1 p-3 space-y-1">
      {navItems.map((item) => {
        const isActive = currentPageName === item.page;
        return (
          <Link
            key={item.page}
            to={createPageUrl(item.page)}
            onClick={() => setMobileOpen(false)}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              isActive ? 'bg-emerald-50 text-emerald-700' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-800'
            }`}
          >
            <item.icon className={`w-4 h-4 ${isActive ? 'text-emerald-600' : 'text-slate-400'}`} />
            {item.name}
          </Link>
        );
      })}
    </nav>
  );

  const SidebarFooter = () => (
    <div className="p-3 border-t border-slate-100 space-y-2">
      {user && (
        <div className="px-2 py-1">
          <p className="text-xs font-medium text-slate-600 truncate">{user.full_name || user.email}</p>
          {user.full_name && <p className="text-xs text-slate-400 truncate">{user.email}</p>}
        </div>
      )}
      <button
        onClick={logout}
        className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-xs font-medium text-slate-500 hover:bg-slate-50 hover:text-slate-700"
      >
        <LogOut className="w-3.5 h-3.5" />
        Sign out
      </button>
      <p className="text-xs text-slate-400 text-center pt-1">© 2026 Trevarthen Finance</p>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-slate-50">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex w-60 bg-white border-r border-slate-200 flex-col fixed h-full z-10">
        <div className="p-5 border-b border-slate-100">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-emerald-600 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="font-bold text-slate-800 text-sm leading-none">Trevarthen Finance</p>
              <p className="text-xs text-slate-400 mt-0.5">Track & Save</p>
            </div>
          </div>
        </div>
        <NavLinks />
        <SidebarFooter />
      </aside>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-20 bg-white border-b border-slate-200 flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 bg-emerald-600 rounded-lg flex items-center justify-center">
            <TrendingUp className="w-3.5 h-3.5 text-white" />
          </div>
          <span className="font-bold text-slate-800 text-sm">Trevarthen Finance</span>
        </div>
        <button onClick={() => setMobileOpen(!mobileOpen)} className="p-1.5 rounded-lg hover:bg-slate-100">
          {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile Sidebar Overlay */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-10 bg-black/30" onClick={() => setMobileOpen(false)}>
          <div className="w-60 h-full bg-white flex flex-col pt-14" onClick={e => e.stopPropagation()}>
            <NavLinks />
            <SidebarFooter />
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 md:ml-60 overflow-auto">
        <div className="pt-[57px] md:pt-0">
          {children}
        </div>
      </main>
    </div>
  );
}
